import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Chip, CircularProgress,
  IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography, InputAdornment,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon    from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon        from "@mui/icons-material/Edit";
import TimerIcon       from "@mui/icons-material/Timer";
import { toast } from "react-toastify";
import api from "../../config/axios";

const PARETIC_SIDE_LABELS = {
  LEFT:  "Izquierda",
  RIGHT: "Derecha",
};

const STROKE_STATUS_LABELS = {
  subagudo_temprano: { label: "Subagudo temprano", color: "warning" },
  subagudo_tardio:   { label: "Subagudo tardío",   color: "info" },
  cronico:           { label: "Crónico",            color: "error" },
};

const STROKE_TYPE_LABELS = {
  isquemico:   "Isquémico",
  hemorragico: "Hemorrágico",
  AIT:         "AIT",
};

function calcAge(dateOfBirth) {
  if (!dateOfBirth) return "—";
  const dob  = new Date(dateOfBirth);
  const diff = Date.now() - dob.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
}

export default function PatientList() {
  const navigate = useNavigate();
  const stored   = localStorage.getItem("user");
  const user     = stored && stored !== "undefined" ? JSON.parse(stored) : {};

  const [patients, setPatients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const { data } = await api.get("/patients");
        const list = Array.isArray(data) ? data : [];
        setPatients(list);
        setFiltered(list);
      } catch (err) {
        toast.error("Error al cargar los pacientes.");
      } finally {
        setLoading(false);
      }
    };
    fetchPatients();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      patients.filter(
        (p) =>
          (p.patient_code || "").toLowerCase().includes(q) ||
          (p.stroke_type  || "").toLowerCase().includes(q) ||
          (p.stroke_status|| "").toLowerCase().includes(q)
      )
    );
  }, [search, patients]);

  return (
    <Box>
      {/* Cabecera */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Pacientes — {user.hospital_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} paciente{filtered.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => navigate("/patients/new")}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          Nuevo paciente
        </Button>
      </Box>

      {/* Buscador */}
      <TextField
        placeholder="Buscar por código, tipo de ictus..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        sx={{ mb: 2, width: { xs: "100%", sm: 340 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Tabla */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "primary.main" }}>
                {[
                  "Código", "Género", "Edad", "Mano parética",
                  "Tipo ictus", "Estado ictus", "Lado afectado",
                  "NIHSS", "ARAT", "FM UE", "Acciones",
                ].map((h) => (
                  <TableCell key={h} sx={{ color: "white", fontWeight: 700, whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No hay pacientes registrados todavía.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((p) => {
                  const statusMeta = STROKE_STATUS_LABELS[p.stroke_status];
                  return (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{p.patient_code || "—"}</TableCell>
                      <TableCell>{p.gender || "—"}</TableCell>
                      <TableCell>{calcAge(p.date_of_birth)}</TableCell>
                      <TableCell>{PARETIC_SIDE_LABELS[p.PARETIC_SIDE] || "—"}</TableCell>
                      <TableCell>{STROKE_TYPE_LABELS[p.stroke_type] || p.stroke_type || "—"}</TableCell>
                      <TableCell>
                        {statusMeta ? (
                          <Chip label={statusMeta.label} color={statusMeta.color} size="small" />
                        ) : "—"}
                      </TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>{p.affected_side || "—"}</TableCell>
                      <TableCell>{p.nihss ?? "—"}</TableCell>
                      <TableCell>{p.arat ?? "—"}</TableCell>
                      <TableCell>{p.fugl_meyer_ue ?? "—"}</TableCell>
                      <TableCell>
                        <Tooltip title="Ver detalle">
                          <IconButton size="small" onClick={() => navigate(`/patients/${p.id}`)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/patients/${p.id}/edit`)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duración de actividades">
                          <IconButton size="small" onClick={() => navigate(`/patients/${p.id}/prescriptions`)}>
                            <TimerIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
